import React, {useState,useEffect, useCallback, useLayoutEffect, useRef} from 'react'
import NavBar from './screens/NavBar/NavBar'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import Footer from './screens/Footer/Footer'
import { CartContext } from './context/Cart';
import UseCookie from './utils/cookies/useCookie';
import Colors from './utils/colors/Colors';
import {LocationContext} from './context/Location'
import {ThemeContext} from './context/Theme'
import { addItemsToCart,removeItemsToCart } from "./apollo/mutations";
import { ViewCart } from "./apollo/queries";
import { useMutation, gql, useApolloClient } from "@apollo/client";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useTranslation } from 'react-i18next';
import { getLocationPermission, getCurrentLocationCoordinates ,getLocationDetails,} from './utils/LocationServices/LocationServices'
import Loader from './ui-components/Loader/Loader';
import { QueryAllCustomerTypes, QueryCustomerDetails } from "./apollo/queries";
import { NestPass } from './ui-components/PremuimBanner/NestPass';
import ErrorBoundary from './ui-components/ErrorBoundary/ErrorBoundary';
import ScrollToTop from './ui-components/ScrollToTop/ScrollToTop';
import PageViewTracker from './ui-components/PageViewTracker/PageViewTracker';
import DetectDeviceTypeRedirect from './screens/DetectDeviceType/DetectDeviceTypeRedirect';

// Lazy Loaded Screens
const HomePage = React.lazy(() => import('./screens/HomePage/HomePage'));
const TermsAndConditions = React.lazy(() => import('./screens/Menu/TermsAndConditions'));
const PrivacyPolicy = React.lazy(() => import('./screens/Menu/PrivacyPolicy'));
const AccountDeletion = React.lazy(() => import('./screens/Menu/AccountDeletion'));
const Categories = React.lazy(() => import('./screens/Categories/Categories'));
const SubCategories = React.lazy(() => import('./screens/Categories/SubCategories'));
const ProductDetails = React.lazy(() => import('./screens/ProductDetails/ProductDetails'));
const ContactUs = React.lazy(() => import('./screens/Menu/ContactUs'));
const SearchProducts = React.lazy(() => import('./screens/HomePage/SearchProducts'));
const RefundAndReturnPolicy = React.lazy(() => import('./screens/Menu/RefundAndReturnPolicy'));
const ShippingPolicy = React.lazy(() => import('./screens/Menu/ShippingPolicy'));
const OrderTransactionStatus = React.lazy(() => import('./screens/PaymentTransactions/OrderTransactionStatus'));
const NestPassTransactionStatus = React.lazy(() => import('./screens/PaymentTransactions/NestPassTransactionStatus'));
const OrderDetails = React.lazy(() => import('./screens/Menu/OrderDetails'));
const PageNotFound = React.lazy(() => import('./screens/PageNotFound/PageNotFound'));


const addItemToCart = gql`
${addItemsToCart}
`;

const removeItemFromCart = gql`
${removeItemsToCart}
`;

const VIEWCART = gql`
${ViewCart}
`;

const getAllCustomerTypes = gql`
  ${QueryAllCustomerTypes}
`;

const getCustomerDetails = gql`
  ${QueryCustomerDetails}
`;

export default function App() {
  const {t} = useTranslation();
  const client = useApolloClient();

  const CART_STORAGE_KEY = 'cartItems';

  var [searchLocation, setSearchLocation] = useState(false)
  const [cartItems, setCartItems] = useState({});
  var [appIsReady, setAppIsReady] = useState(false)
  const [location, setLocation] = useState(null)
  var [appColorTheme, setAppColorTheme] = useState()
  var [loadingCartDetails, setLoadingCartDetails] = useState(false)
  const [loading, setLoading] = useState(false);
  const [networkStatus, setNetworkStatus] = useState(window.navigator.onLine)
  const cartRef = useRef(cartItems);
  const systemThemeMode = (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)?'dark':'light';
  const colors = Colors[appColorTheme=='systemDefault'?systemThemeMode:appColorTheme]

  const [ADDITEMTOCART] = useMutation(addItemToCart, { onCompleted, onError });
  const [REMOVEITEMFROMCART] = useMutation(removeItemFromCart, { removeItemCompleted, removeItemError });

  async function onCompleted({ login }) {
  }
  async function removeItemCompleted({ login }) {
  }

  function onError(errors) {
  }

  function removeItemError(errors) {
  }

  const { getCustomCookie, setCustomCookie, clearAllCookies } = UseCookie();

  const emptyCart = { items: [], cartobj: {} };

  const parseCartValue = useCallback((rawValue) => {
    if (!rawValue) return emptyCart;

    try {
      const parsed = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;

      if (parsed && typeof parsed === 'object') {
        return {
          ...parsed,
          items: Array.isArray(parsed?.items) ? parsed.items : [],
          cartobj: parsed?.cartobj && typeof parsed.cartobj === 'object' ? parsed.cartobj : {},
        };
      }
    } catch (err) {
      console.error('Failed to parse cart from storage', err);
    }

    return emptyCart;
  }, []);

  const readCartFromStorage = useCallback(() => {
    try {
      const lsValue = localStorage.getItem(CART_STORAGE_KEY);
      if (lsValue) return parseCartValue(lsValue);
    } catch (err) {
      console.warn('Unable to read cart from localStorage', err);
    }

    try {
      const cookieCart = getCustomCookie(CART_STORAGE_KEY);
      if (cookieCart) return parseCartValue(cookieCart);
    } catch (err) {
      console.warn('Unable to read cart from cookie', err);
    }

    return emptyCart;
  }, [getCustomCookie, parseCartValue]);

  const persistCart = useCallback(async (cartData) => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
    } catch (err) {
      console.warn('Unable to persist cart to localStorage', err);
    }

    try {
      await setCustomCookie(CART_STORAGE_KEY, JSON.stringify(cartData));
    } catch (err) {
      console.warn('Unable to persist cart to cookie', err);
    }
  }, [setCustomCookie]);


  useEffect(()=>{
    const loadAppData = async () => {
      await checkLoginInfo()
      await checkNetworkStatus()
      await checkThemeMode()
      await getAnStoreLocationInfo()
      setAppIsReady(true)
    }
    loadAppData()
    return () => {}
  },[])

  const checkLoginInfo = async () => {
    const loginInfo = await getCustomCookie("loginInfo");
    const cartItemsValue = readCartFromStorage();

    // Always hydrate state from storage first so UI has the latest cart
    setCartItems(cartItemsValue);

    // If not logged in -> nothing else to do
    if (!loginInfo || typeof loginInfo !== 'object' || Object.keys(loginInfo).length === 0) {
      return;
    }

    // If logged in -> keep your existing flow
    await fetchCustomerDetails(loginInfo);
  };


  const fetchAllCustomerTypes = async(loginInfo)=>{
    try{
      const { data } = await client.query({
        query: getAllCustomerTypes,
        variables:{},
        fetchPolicy: 'no-cache',
      });
      await setCustomCookie('allCustomerTypes',JSON.stringify(data?.getAllCustomerTypes))
      let generalCustomerType = await fetchGeneralCustomerTypeIfPremium(data?.getAllCustomerTypes,loginInfo)
      await setCustomCookie('generalCustomerType',JSON.stringify(generalCustomerType))
    }
    catch(error){
      toast.error("Error to fetch all customer types")
    }
  }

  //This works only for a premuim customer and return general customer type
  const fetchGeneralCustomerTypeIfPremium = async(allCustomerTypes,loginInfo)=>{
    let index = allCustomerTypes?.findIndex(data=>data?.default == true) //default true indicates that customer is a general customer , not premium customer
    if(loginInfo?.customerType != allCustomerTypes[index]?.customerType){
      return allCustomerTypes[index]?.customerType
    }
    //this is to display general customer price, along with premium price
  }

  const fetchCustomerDetails = async(loginInfo)=>{
    try{
      const { data } = await client.query({
        query: getCustomerDetails,
        variables:{},
        fetchPolicy: 'no-cache',
      });
      await setCustomCookie('loginInfo',JSON.stringify({...data?.queryCustomer,jwtToken:loginInfo?.jwtToken}))
      await fetchAllCustomerTypes({...data?.queryCustomer,jwtToken:loginInfo?.jwtToken}) //to get updated customer types - every time when user opens the app
    }
    catch(error){
      toast?.error("Error to fetch customer details")
    }
  }
  
  const checkNetworkStatus = ()=>{
    const handleOnlineStatus = () => setNetworkStatus(true);
    const handleOfflineStatus = () => setNetworkStatus(false);
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOfflineStatus);
  }

  const checkThemeMode = async ()=>{
    let theme = await getCustomCookie('theme');
    setAppColorTheme(theme);
  }

  const getAnStoreLocationInfo = async()=>{
    try {
      let locationInfo = await getCustomCookie('locationInfo')
      if (Object.keys(locationInfo).length!=0) {
        setLocation(locationInfo)
      }
    } catch (err) {
    }
  }

  useEffect(() => {
    cartRef.current = cartItems;
  }, [cartItems])

  useLayoutEffect(() => {
    assignThemeMode(appColorTheme)
  }, [appColorTheme])

    //save location details to local storage
    useEffect(() => {
      if (!location) return
  
      const saveLocation = async () => {
        await setCustomCookie('locationInfo', JSON.stringify(location))
      }
  
      saveLocation()
    }, [location])

  const assignThemeMode = useCallback(async (appColorTheme)=>{
    if (!appColorTheme) {
      let theme = 'systemDefault'
      setAppColorTheme(theme)
      
      await saveAppColorTheme(theme) // systemThemeMode
    }
    else{
      await saveAppColorTheme(appColorTheme)
    }
  },[])

  const saveAppColorTheme = useCallback(async (theme) => {
    await setCustomCookie('theme', JSON.stringify(theme))
    try{
      if(theme === 'dark'){
        document.documentElement.classList.add('dark');
      }
      else{
        document.documentElement.classList.remove('dark');
      }
    }
    catch(error){
      toast.error(error)
    }
  },[])

  //Manage Cart Data

  const addToCart = useCallback(async (item, index, isLocationServicable, loginInfo, locationInfo) => {
    try {
      if (!isLocationServicable) {
        toast.error("Can't Add, Service not available for the selected location. Please change the address on the home page");
        return;
      }

      const latestCart = cartRef.current || { items: [], cartobj: {} };

      item = {
        ...item,
        quantity: Number.isInteger(item?.quantity) ? item.quantity + 1 : 1,
        itemId: item?.itemId ? item.itemId : item?.productId,
        productId: item?.itemId ? item.itemId : item?.productId,
      };

      let cartDataObject = JSON.parse(JSON.stringify(latestCart));
      cartDataObject = { ...cartDataObject, cartobj: { ...(cartDataObject.cartobj || {}), [item.itemId]: item } };

      cartDataObject.items = cartDataObject.items || [];
      if (isNaN(index)) {
        const i = cartDataObject.items.findIndex(d => (d.itemId ?? d.productId) == (item.itemId ?? item.productId));
        if (i !== -1) cartDataObject.items[i] = item;
        else cartDataObject.items = cartDataObject.items.concat(item);
      } else {
        cartDataObject.items[index] = item;
      }

      setCartItems(cartDataObject);
      await persistCart(cartDataObject);

      if (loginInfo && Object.keys(loginInfo).length !== 0) {
        await updateCartItems(item, loginInfo, locationInfo);
      }
    } catch (error) {
    }
  }, [persistCart]);

 
  
  
  const removeFromCart = useCallback(async (item, index, isLocationServicable, loginInfo, locationInfo) => {
    if (!isLocationServicable) {
      toast.error("Can't Remove, Service not available for the selected location. Please change the address on the home page", {
        position: "top-right",
        autoClose: 5000,
        theme: systemThemeMode,
      });
      return;
    }

    const latestCart = cartRef.current || { items: [], cartobj: {} };

    if (item?.quantity === 1) {
      let updatedCartItems = JSON.parse(JSON.stringify(latestCart));

      const key = item?.itemId ?? item?.productId;
      if (updatedCartItems?.cartobj) delete updatedCartItems.cartobj[key];

      if (isNaN(index)) {
        const idx = (updatedCartItems.items || []).findIndex(d => (d.itemId ?? d.productId) === key);
        index = idx;
      }

      if (Array.isArray(updatedCartItems.items) && index >= 0) {
        updatedCartItems.items = await deleteElementByIndex(updatedCartItems.items, index);
      } else {
        updatedCartItems.items = updatedCartItems.items || [];
      }

      setCartItems(updatedCartItems);
      await persistCart(updatedCartItems);

      if (loginInfo && Object.keys(loginInfo).length !== 0) {
        await deleteItemFromCart(item, loginInfo, locationInfo);
      }
      return;
    }

    // quantity > 1
    const nextItem = {
      ...item,
      quantity: item.quantity - 1,
      itemId: item?.itemId ? item.itemId : item?.productId,
      productId: item?.itemId ? item.itemId : item?.productId,
    };

    let cartDataObject = JSON.parse(JSON.stringify(latestCart));
    cartDataObject = { ...cartDataObject, cartobj: { ...(cartDataObject.cartobj || {}), [nextItem.itemId]: nextItem } };

    cartDataObject.items = cartDataObject.items || [];
    if (isNaN(index)) {
      const i = cartDataObject.items.findIndex(d => (d.itemId ?? d.productId) === (nextItem.itemId ?? nextItem.productId));
      if (i !== -1) cartDataObject.items[i] = nextItem;
      else cartDataObject.items = cartDataObject.items.concat(nextItem);
    } else {
      cartDataObject.items[index] = nextItem;
    }

    setCartItems(cartDataObject);
    await persistCart(cartDataObject);

    if (loginInfo && Object.keys(loginInfo).length !== 0) {
      await updateCartItems(nextItem, loginInfo, locationInfo);
    }
  }, [persistCart]);

  
  const  deleteElementByIndex = (arr, index)=>{
    if (index < 0 || index >= arr.length) {
      throw new Error('Index out of bounds');
    }
    for (let i = index; i < arr.length - 1; i++) {
        arr[i] = arr[i + 1];
      }
      if(arr?.length>0){
        arr.length--; // Reduce the length of the array by 1 to remove the last element
      }
    return arr.slice(0, arr.length); // Return a new array without the last element
  }

  const updateCartItems = async(item,loginInfo, locationInfo) => {
    let requestBody = {
      "cartAddInputs": {
        "itemInputs": [
              {
                "productId": item?.itemId?item?.itemId:item?.productId,
                "quantity": item?.quantity,
                "skuId": item?.skuId
              }
            ]
          }
        }
    try{
      let response = await ADDITEMTOCART({ variables:requestBody})
      if(response?.cartDetails) {
      }
    }catch(error){
    }
  }

  const deleteItemFromCart = async(item,loginInfo,locationInfo)=>{

    let requestBody = {"item":{
      "productId": item?.itemId?item?.itemId:item?.productId,
      "quantity": item?.quantity,
      "skuId": item?.skuId
    }}
  try{
    let response = await REMOVEITEMFROMCART({ variables:requestBody})
    if(response?.cartDetails){
    }
  }catch(error){
  }
  }

  const callCartData = useCallback(async (cartItems,loginInfo, locationInfo) => {
    if(loginInfo && Object.keys(loginInfo)?.length !=0){
      	try {
          setLoadingCartDetails(true)
          let requestBody= {
            "longitude": ""+((locationInfo &&locationInfo?.longitude)?locationInfo?.longitude:"77.59104670126453"),
            "latitude": ""+((locationInfo && locationInfo?.latitude)?locationInfo?.latitude:"13.04269368886535"),
            }

          const { data } = await client.query({
            query: VIEWCART,
            variables:requestBody,
            fetchPolicy: 'network-only',
          });
          if (data?.queryCartV2) {
            setCartData(data.queryCartV2.cart, null, cartItems);
          }
          setLoadingCartDetails(false)
        } catch (error) {
        }
    	}
    	else{
      	await setCartData(null,false,cartItems)
    	}
  	},[]);

    const setCartData = useCallback( async (cartDetails, dataEmpty,cartItems) => {
      if (cartDetails) {
        let cartData = cartDetails;
        if(cartDetails?.items?.length>0){
          let itemsById = cartData?.items?.reduce((acc, item) => {
            acc[item?.itemId ?? item?.productId] = item;
            return acc;
          }, {});
          cartData = { ...cartData, cartobj: itemsById };
        }
        else{
          cartData = { ...cartDetails,items: [], cartobj: {} };
        }
        setCartItems(cartData);
        await persistCart(cartData);
      } else {
        if (cartItems && Object.keys(cartItems).length > 0 && !dataEmpty) {
          setCartItems({ ...cartItems });
          await persistCart({ ...cartItems });
        }
        else if(cartItems && Object.keys(cartItems).length > 0 && dataEmpty){
          const updatedCart = { ...cartItems,items: [], cartobj: {} };
          setCartItems(updatedCart);
          await persistCart(updatedCart);
        } else {
          setCartItems({ items: [], cartobj: {} });
          await persistCart({ items: [], cartobj: {} });
        }
      }
    },[persistCart]);

  const handleGetLocation = async () => {
    try{
      setLoading(true)
      let permissions = await getLocationPermission()
      if(permissions){
        let coordinates = await getCurrentLocationCoordinates()
        if(coordinates){
          setLoading(false)
          let locationDetails = await getLocationDetails(coordinates)
          setLocation(locationDetails)
        }
      }
    }
    catch(error){
      toast.error(error)
    }
  }


  // const handleClickOutside = (event) => {if (modalRef.current && !modalRef.current.contains(event.target)) {closeModal();}};

  // const openModal = () => {
  //     setShowLocationModal(true);
  //     document.addEventListener('mousedown', handleClickOutside);
  // };

  // const closeModal = () => {
  //  //console.log("Welcome puuja")
  //     setShowLocationModal(false);
  //     document.removeEventListener('mousedown', handleClickOutside);
  // };

  const RedirectToSubCategories = () => {
    const { id } = useParams();
    return <Navigate to={`/sub-categories/${id}`} replace />;
  };

  const RedirectToItemDetails = () => {
    const { id } = useParams();
    return <Navigate to={`/item-details/${id}`} replace />;
  };

  const RedirectToCategories = () => {
    return <Navigate to={`/categories-list`} replace />;
  };



  if (appIsReady) {
  return (
    <>
      <LocationContext.Provider value={{ location, setLocation }}>
        <ThemeContext.Provider value={{ systemThemeMode,appColorTheme, setAppColorTheme }}>  
          <CartContext.Provider value={{ cartItems, loadingCartDetails,setCartItems, addToCart,removeFromCart, setCartData, callCartData }}>
            <Loader loading={loading} />
            <div className='body'>
            <ToastContainer 
            theme={appColorTheme=='systemDefault'?systemThemeMode:appColorTheme} />
              <BrowserRouter>
                <ScrollToTop />
                <PageViewTracker />
                <NavBar handleGetLocation={handleGetLocation} searchLocation={searchLocation} setSearchLocation={setSearchLocation}/>
                  <div style={{backgroundColor:Colors.colorTheme[colors?.homePageBg]}} className={`pt-32 xs:pt-32 sm:pt-32 md:pt-20 lg:pt-20 xl:pt-20 xxl:pt-20 pb-2  bg-${colors?.homePageBg} shadow-slate-500 dark:bg-${colors?.homePageBg} min-h-screen`}>
                    <div className='mx-2p sm:mx-10p md:mx-10p lg:mx-10p xl:mx-10p'>
                      <ErrorBoundary>
                        <React.Suspense fallback={<div className="flex justify-center items-center min-h-screen"><Loader loading={true} /></div>}>
                          <Routes>
                            <Route path="/" element={<HomePage/>} />
                            <Route path="/apps" element={<DetectDeviceTypeRedirect/>} />
                            <Route path="/PrivacyPolicy" element={<PrivacyPolicy/>} />
                            <Route path="/account-deletion" element={<AccountDeletion/>} />
                            <Route path="/DeleteAccount" element={<Navigate to="/account-deletion" replace />} />
                            <Route path="/TermsAndConditions" element={<TermsAndConditions/>} />
                            <Route path="/RefundAndReturnPolicy" element={<RefundAndReturnPolicy/>} />
                            <Route path="/ShippingPolicy" element={<ShippingPolicy/>} />
                            <Route path="/ContactUs" element={<ContactUs/>} />
                            <Route path="/SearchProducts" element={<SearchProducts/>} />
                            <Route path="/categories-list" element={<Categories/>} />
                            <Route path="/Categories" element={<RedirectToCategories />} />
                            <Route path="/sub-categories/:id" element={<SubCategories/>} />
                            <Route path="/SubCategories/:id" element={<RedirectToSubCategories/>} />
                            <Route path="/item-details/:id" element={<ProductDetails/>} />
                            <Route path="/productDetails/:id" element={<RedirectToItemDetails />} />
                            <Route path="/purchase-nest-pass" element={<NestPass openPremuimBanner={()=>{}} colors={colors} isNestPassPage={true}  />} />
                            <Route path="/Order/Payment-Status/:id" element={<OrderTransactionStatus/>} />
                            <Route path="/Order/OrderDetails/:id" element={<OrderDetails/>} />
                            <Route path="/NestPass/Payment-Status/:id" element={<NestPassTransactionStatus/>} />
                            <Route path="/app">
                              <Route path="categories-list" element={<Categories/>} />
                              <Route path="sub-categories/:id" element={<SubCategories/>} />
                              <Route path="item-details/:id" element={<ProductDetails/>} />
                              <Route path="purchase-nest-pass" element={<NestPass openPremuimBanner={()=>{}} colors={colors} isNestPassPage={true}  />} />
                              <Route path="order-details/:id" element={<OrderDetails/>} />
                            </Route>
                            <Route path="*" element={<PageNotFound />} />
                          </Routes>
                        </React.Suspense>
                      </ErrorBoundary>
                    </div>
                  </div>
                { <Footer/>}
              </BrowserRouter>
            </div>
          </CartContext.Provider>
        </ThemeContext.Provider>
      </LocationContext.Provider>
    </>
    
  )
  }
  else{
    return <></>
  }
}
